/**
 * Redirecionamento e sessão pós-autenticação (login/cadastro).
 */

import {
  getDefaultRouteByRole,
  normalizeAuthRole,
} from './roleRouting';
import { DOCTOR_ONBOARDING_CACHE_KEY } from './doctorOnboarding';

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  createdAt?: string;
  updatedAt?: string;
};

const CHECKLIST_CACHE_KEY = (userId: string) => `checklist_status_${userId}`;
const ACCESS_TOKEN_KEY = 'accessToken';

export { normalizeAuthRole, getDefaultRouteByRole };

/** Destino após cadastro bem-sucedido. */
export function resolvePostRegisterRedirect(role?: string | null): string {
  const normalized = normalizeAuthRole(role);
  if (normalized === 'PATIENT') {
    return '/onboarding/checklist';
  }
  if (normalized === 'DOCTOR') {
    return '/onboarding/doctor-checklist';
  }
  return getDefaultRouteByRole(role);
}

/**
 * Persiste token + usuário de forma síncrona antes de qualquer navigate/API.
 * Paciente novo: marca checklist pendente no cache local (evita fetch sem sessão).
 * Médico novo: marca onboarding pendente (docs ainda não enviados).
 */
export function establishClientSession(params: {
  accessToken: string;
  user: SessionUser;
  markPatientChecklistPending?: boolean;
  markDoctorOnboardingPending?: boolean;
}): void {
  const token = String(params.accessToken || '').trim();
  if (!token || token.length < 10) {
    throw new Error('Token de sessão inválido');
  }

  let cleanToken = token;
  if (cleanToken.startsWith('"') && cleanToken.endsWith('"')) {
    cleanToken = cleanToken.substring(1, cleanToken.length - 1);
  }

  const role = normalizeAuthRole(params.user.role);
  const user: SessionUser = {
    ...params.user,
    role,
  };

  localStorage.setItem(ACCESS_TOKEN_KEY, cleanToken);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('user_role', role);

  const shouldMarkPatientChecklist =
    params.markPatientChecklistPending !== false && role === 'PATIENT' && !!user.id;

  if (shouldMarkPatientChecklist) {
    localStorage.setItem(CHECKLIST_CACHE_KEY(user.id), 'true');
  }

  const shouldMarkDoctorOnboarding =
    params.markDoctorOnboardingPending === true && role === 'DOCTOR' && !!user.id;

  if (shouldMarkDoctorOnboarding) {
    localStorage.setItem(DOCTOR_ONBOARDING_CACHE_KEY(user.id), 'true');
  }
}

export function hasPersistedAuthSession(): boolean {
  if (typeof window === 'undefined') return false;
  const token =
    localStorage.getItem(ACCESS_TOKEN_KEY) || localStorage.getItem('auth_token');
  const user = localStorage.getItem('user');
  return !!(token && token !== 'undefined' && token !== 'null' && user);
}
